import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import Img from '../../../components/Img';
import { divisionFormatTypeIds, drawCategoryTypeIds } from '../../../utils/eventConstants';
import { removeDrawFromFacilityAndDayConfig, startDrawAddedToFacilityAndDayConfig } from '../../../actions/next-available-actions';
import { hasChildDraws } from '../../../utils/courtSchedular';
import { dateFormatForDrawDisplay, DefaultNotificationData, DrawStatusTypes, getChildDrawNotificationData, getDrawStatusNotificationTextDetails } from '../../../utils/nextAvailable';

import styles from './nextAvailable.scss';
import ConfirmationPopup from './ConfirmationPopup';
import { getLabelByTypeId } from '../../../utils/types';

const DrawListItem = (props) => {
    const {
        statusType,
        draw,
        parentDraw,
        event,
        className,
        onDrawSettingsEdit,
        onDrawClick,
        selectedDrawIds,
        'data-testid': dataTestId,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);

    const {
        division: {
            tournament: {
                drawEliminationTypes,
            },
        },
    } = coreUI.types.tms;

    const { eventSchedule } = event;
    const dispatch = useDispatch();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [confirmDeleteMessage, setConfirmDeleteMessage] = useState(null);
    const [showConfirmStart, setShowConfirmStart] = useState(false);

    const drawHasChildren = draw.hasChildDraws;

    const onClickItem = () => {
        if (drawHasChildren) {
            onDrawClick(draw);
        }
    };

    const isDrawOpen = useMemo(() => {
        return selectedDrawIds?.includes(draw.customDrawId);
    }, [selectedDrawIds, draw]);

    const shouldDisplayDeleteIcon = useMemo(() => {
        return (statusType === DrawStatusTypes.TODO || statusType === DrawStatusTypes.LIVE) && draw.canDeleteDraw;
    }, [draw]);

    const shouldDisplayStartIcon = useMemo(() => {
        return statusType === DrawStatusTypes.TODO && draw.canStartDraw;
    }, [draw]);

    const shouldDisableStartIcon = useMemo(() => {
        return !draw.canStartDraw;
    }, [draw]);

    const formattedStartDate = useMemo(() => {
        if (draw.categoryTypeId === drawCategoryTypeIds.GROUPED) {
            const childDrawsWithStartDates = draw.childDraws.filter((childDraw) => childDraw.startDateTimeUtc);

            if (childDrawsWithStartDates?.length > 0) {
                const dateTimeUtc = childDrawsWithStartDates[0].startDateTimeUtc;
                return moment.utc(dateTimeUtc).tz(eventSchedule.timeZone?.value).format(dateFormatForDrawDisplay);
            }

            return '';
        }

        let dateTimeUtc = draw.startDateTimeUtc;
        if (parentDraw && parentDraw?.categoryTypeId !== drawCategoryTypeIds.GROUPED) {
            if (parentDraw?.categoryTypeId === drawCategoryTypeIds.PRO_DRAW) {
                dateTimeUtc = draw.startDateTimeUtc ?? parentDraw.startDateTimeUtc;
            } else {
                dateTimeUtc = parentDraw.startDateTimeUtc;
            }
        }

        if (draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW && !draw.isSelected) {
            dateTimeUtc = null;
        }

        if (dateTimeUtc) {
            return moment.utc(dateTimeUtc).tz(eventSchedule.timeZone?.value).format(dateFormatForDrawDisplay);
        }

        return null;
    }, [draw, parentDraw]);

    const matchAndTeamString = useMemo(() => {
        return `${draw.participantCount} ${draw.formatTypeId === divisionFormatTypeIds.SINGLES ? 'players' : 'teams'}, ${draw.matchCount} matches`;
    }, [draw]);

    const displayableChildDraws = useMemo(() => {
        if (hasChildDraws(draw)) {
            return draw.childDraws.filter((childDraw) => {
                return childDraw.drawStatusToCheck.includes(statusType);
            });
        }

        return [];
    }, [draw, statusType]);

    const drawNotificationData = useMemo(() => {
        const isTodoParentDraw = statusType === DrawStatusTypes.TODO && draw.customDrawId.startsWith('parent-');

        if (!isTodoParentDraw) {
            return DefaultNotificationData;
        }

        if (draw.childDraws?.length > 0) {
            return getChildDrawNotificationData(draw.childDraws);
        }

        const shouldDisplay = draw.drawStatus === DrawStatusTypes.TODO && !!draw.drawStatusNotification;
        if (shouldDisplay) {
            const { text, color } = getDrawStatusNotificationTextDetails(draw.drawStatusNotification);
            return { shouldDisplay, text, color };
        }

        return DefaultNotificationData;
    }, [draw, statusType]);

    const onClickSettings = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        onDrawSettingsEdit(draw);
    };

    const onClickDelete = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        setShowConfirmDelete(true);
        setConfirmDeleteMessage(statusType === DrawStatusTypes.LIVE
            ? 'Do you want to remove this draw from \'Live\' status? It will be moved back to the \'To Do\' column.'
            : 'Do you want to remove the draw from this facility and day?');
    };

    const onCloseDeleteConfirmPopup = () => {
        setShowConfirmDelete(false);
        setConfirmDeleteMessage(null);
    };

    const onConfirmDelete = () => {
        let drawIds = [];
        if (displayableChildDraws?.length > 0) {
            drawIds = displayableChildDraws
                .filter((childDraw) => childDraw.canDeleteDraw)
                .map((childDraw) => childDraw.id);
        } else {
            drawIds = [draw.id];
        }
        dispatch(removeDrawFromFacilityAndDayConfig(event?.id, drawIds));
        onCloseDeleteConfirmPopup();
    };

    const onClickStart = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setShowConfirmStart(true);
    };

    const onCloseStartConfirmPopup = () => {
        setShowConfirmStart(false);
    };

    const onConfirmStart = useCallback((drawStartNotify) => {
        let drawIds = [];
        if (displayableChildDraws?.length > 0) {
            drawIds = displayableChildDraws
                .filter((childDraw) => childDraw.canStartDraw)
                .map((childDraw) => childDraw.id);
        } else {
            drawIds = [draw.id];
        }
        dispatch(startDrawAddedToFacilityAndDayConfig(event?.id, drawIds, drawStartNotify));
        onCloseStartConfirmPopup();
    }, [draw, displayableChildDraws]);

    return (
        <>
            <div className={`${styles.drawListItem} ${className || ''}`} data-testid={dataTestId}>
                {drawNotificationData?.shouldDisplay && (
                    <div className={styles.drawStatusNotification} style={{ color: drawNotificationData?.color }} data-testid={dataTestId && `${dataTestId}.notification`}>
                        {drawNotificationData?.text}
                    </div>
                )}
                <div
                    className={`d-flex align-items-start ${styles.parentSection}`}
                    onClick={onClickItem}
                >
                    {!parentDraw && (
                        <div className={styles.arrow}>
                            {drawHasChildren && isDrawOpen && (
                                <i className={`fas fa-solid fa-caret-down`} />
                            )}
                            {drawHasChildren && !isDrawOpen && (
                                <i className={`fas fa-solid fa-caret-right`} />
                            )}
                        </div>
                    )}
                    <div className={`flex-1 mw0 ${parentDraw ? '' : 'ml4'} mr4`}>
                        <div className={styles.name} data-testid={dataTestId && `${dataTestId}.name`}>
                            {draw.name}
                        </div>
                        {formattedStartDate && (
                            <div className={`${styles.description} mt8`} data-testid={dataTestId && `${dataTestId}.date`}>
                                {formattedStartDate}
                            </div>
                        )}
                        {(!parentDraw || draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW_QUALIFIER) && (
                            <div className={`${styles.description} ${formattedStartDate ? 'mt4' : 'mt8'}`} data-testid={dataTestId && `${dataTestId}.format`}>
                                {getLabelByTypeId(drawEliminationTypes, draw.eliminationTypeId)}
                            </div>
                        )}
                        {matchAndTeamString && (
                            <div className={`${styles.description} ${(formattedStartDate || !parentDraw) ? 'mt4' : 'mt8'}`} data-testid={dataTestId && `${dataTestId}.players-matches`}>
                                {matchAndTeamString}
                            </div>
                        )}
                    </div>
                    <div className={styles.overlay}>
                        {shouldDisplayStartIcon && (
                            <button
                                className={`btn ${styles.drawSettings} p0 mr4`}
                                type="button"
                                onClick={onClickStart}
                                disabled={shouldDisableStartIcon}
                                data-testid={dataTestId && `${dataTestId}.start-btn`}
                            >
                                <Img src="/img/v3/icons/icon-12px-start-blue.svg" />
                            </button>
                        )}
                        {draw.canEditDraw && (
                            <button
                                className={`btn ${styles.drawSettings} p0 mr4`}
                                type="button"
                                onClick={onClickSettings}
                                data-testid={dataTestId && `${dataTestId}.settings-btn`}
                            >
                                <Img src="/img/v3/icons/icon-24px-settings-blue.svg" />
                            </button>
                        )}
                        {shouldDisplayDeleteIcon && (
                            <button
                                className={`btn ${styles.drawSettings} p0 mr4`}
                                type="button"
                                onClick={onClickDelete}
                                data-testid={dataTestId && `${dataTestId}.delete-btn`}
                            >
                                <Img src="/img/v3/icons/icon-12px-delete-blue.svg" />
                            </button>
                        )}
                    </div>
                </div>
                {isDrawOpen && (
                    <div className={`d-flex align-items-start ${styles.childSection}`}>
                        <div className={`${styles.arrow}`} />
                        <div className="flex-1 mw0 ml4">
                            {displayableChildDraws.map((childDraw) => {
                                return (
                                    <DrawListItem
                                        key={`child-draw-${childDraw.id}`}
                                        draw={childDraw}
                                        parentDraw={draw}
                                        event={event}
                                        className={styles.childDraw}
                                        onDrawSettingsEdit={onDrawSettingsEdit}
                                        statusType={statusType}
                                        onDrawClick={onDrawClick}
                                        selectedDrawIds={selectedDrawIds}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            {showConfirmDelete && (
                <ConfirmationPopup
                    description={confirmDeleteMessage}
                    cancelText="No, Cancel"
                    confirmText="Yes, Delete"
                    onConfirm={onConfirmDelete}
                    onCancel={onCloseDeleteConfirmPopup}
                    data-testid="modal.next-available.confirm-delete"
                />
            )}
            {showConfirmStart && (
                <ConfirmationPopup
                    title="Start draw"
                    description={`Would you like to start matches for ${draw.name}? Doing so will add all of the matches in this draw into the queue.`}
                    cancelText="No, Cancel"
                    confirmText="Yes, Start"
                    onConfirm={onConfirmStart}
                    onCancel={onCloseStartConfirmPopup}
                    needsCheckbox
                    checkboxText="Notify players"
                    data-testid="modal.next-available.confirm-start"
                />
            )}
        </>
    );
};

DrawListItem.propTypes = {
    statusType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    draw: PropTypes.objectOf(any),
    parentDraw: PropTypes.objectOf(any),
    event: PropTypes.objectOf(PropTypes.any).isRequired,
    className: PropTypes.string,
    onDrawSettingsEdit: PropTypes.func.isRequired,
    onDrawClick: PropTypes.func.isRequired,
    selectedDrawIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    'data-testid': PropTypes.string,
};

DrawListItem.defaultProps = {
    draw: {},
    parentDraw: null,
    className: '',
    'data-testid': undefined,
};

export default DrawListItem;
